<template>
  <div class="country-code-selector" v-click-outside="closeDropdown">
    <div
      class="selector-display"
      :class="{ 'open': isOpen, 'error': hasError }"
      @click="toggleDropdown"
    >
      <span class="selected-country">
        <span :class="`fi fi-${selectedCountry.iso2}`" class="flag-icon"></span>
        <span class="code">+{{ displayCode }}</span>
      </span>
      <svg class="dropdown-icon" :class="{ 'rotate': isOpen }" width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
        <path d="M6 9L1 4h10L6 9z"/>
      </svg>
    </div>

    <div v-if="isOpen" class="dropdown-menu">
      <div class="search-wrapper">
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          placeholder="Search country..."
          class="search-input"
          @click.stop
        />
      </div>
      <div class="options-list">
        <div
          v-for="country in filteredCountries"
          :key="country.code"
          class="option-item"
          :class="{ 'selected': country.code === modelValue }"
          @click="selectCountry(country)"
        >
          <span :class="`fi fi-${country.iso2}`" class="flag-icon"></span>
          <span class="country-name">{{ country.name }}</span>
          <span class="country-code">+{{ country.code === '20' ? '2' : country.code }}</span>
        </div>
        <div v-if="filteredCountries.length === 0" class="no-results">
          No countries found
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { countryCodes } from '@/utils/countryCodes'

export default {
  name: 'CountryCodeSelector',
  props: {
    modelValue: {
      type: String,
      required: true
    },
    hasError: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  data () {
    return {
      countryCodes,
      isOpen: false,
      searchQuery: ''
    }
  },
  computed: {
    selectedCountry () {
      return this.countryCodes.find(c => c.code === this.modelValue) || this.countryCodes[0]
    },
    displayCode () {
      // Special case for Egypt: display "2" instead of "20"
      return this.modelValue === '20' ? '2' : this.modelValue
    },
    filteredCountries () {
      if (!this.searchQuery) {
        return this.countryCodes
      }
      const query = this.searchQuery.toLowerCase()
      return this.countryCodes.filter(country =>
        country.name.toLowerCase().includes(query) ||
        country.code.includes(query)
      )
    }
  },
  methods: {
    toggleDropdown () {
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus()
        })
      }
    },
    closeDropdown () {
      this.isOpen = false
      this.searchQuery = ''
    },
    selectCountry (country) {
      this.$emit('update:modelValue', country.code)
      this.closeDropdown()
    }
  },
  directives: {
    'click-outside': {
      mounted (el, binding) {
        el.clickOutsideEvent = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value()
          }
        }
        document.addEventListener('click', el.clickOutsideEvent)
      },
      unmounted (el) {
        document.removeEventListener('click', el.clickOutsideEvent)
      }
    }
  }
}
</script>

<style scoped>
.country-code-selector {
  position: relative;
  width: 120px;
}

.selector-display {
  height: 54px;
  padding: 0 12px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s;
  user-select: none;
}

.selector-display:hover {
  border-color: #d1d5db;
}

.selector-display.open {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.selector-display.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.selected-country {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.flag-icon {
  width: 24px;
  height: 18px;
  border-radius: 2px;
  flex-shrink: 0;
}

.code {
  font-weight: 500;
  color: #1f2937;
}

.dropdown-icon {
  transition: transform 0.2s;
  color: #6b7280;
}

.dropdown-icon.rotate {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 280px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 320px;
  display: flex;
  flex-direction: column;
  animation: slideDown 0.2s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.search-wrapper {
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
}

.search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.options-list {
  overflow-y: auto;
  max-height: 260px;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.15s;
}

.option-item:hover {
  background: #f3f4f6;
}

.option-item.selected {
  background: #eff6ff;
  color: #3b82f6;
}

.option-item .flag-icon {
  width: 24px;
  height: 18px;
  border-radius: 2px;
  flex-shrink: 0;
}

.option-item .country-name {
  flex: 1;
  font-size: 14px;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option-item.selected .country-name {
  color: #3b82f6;
  font-weight: 500;
}

.option-item .country-code {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.option-item.selected .country-code {
  color: #3b82f6;
}

.no-results {
  padding: 20px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
}

/* Scrollbar styling */
.options-list::-webkit-scrollbar {
  width: 6px;
}

.options-list::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 3px;
}

.options-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.options-list::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
